export const HEADER_STATES = Object.freeze({
  DISABLED: "disabled",
  UNCHECKED: "unchecked",
  PLUS: "plus",
  MINUS: "minus",
});

const visibleSelectedIdsFrom = (rows, selectedIds) => {
  if (!rows.length || !selectedIds.length) return [];
  const visible = new Set(rows.map((row) => row._id));
  return selectedIds.filter((id) => visible.has(id));
};

const uniqueStatusesFrom = (rows) => {
  const seen = new Set();
  const statuses = [];
  for (const row of rows) {
    if (!row.status || seen.has(row.status)) continue;
    seen.add(row.status);
    statuses.push(row.status);
  }
  return statuses;
};

/**
 * Same-status bulk select for the Promotions eligibility table.
 *
 * - Header stays disabled while visible rows mix statuses and nothing is selected.
 * - Picking one row (or filtering to one status) unlocks header select-all
 *   for matching visible rows only.
 * - Incompatible rows become unselectable until the selection is cleared.
 * - Rows without status (createEvent lean DTO) are treated as CONTACT_PENDING.
 */
const rowStatusOf = (row) => row?.status || "CONTACT_PENDING";

export const getPromotionSelectionModel = (rows = [], selectedIds = []) => {
  const normalized = rows.map((row) => ({
    ...row,
    status: rowStatusOf(row),
  }));
  const visibleSelectedIds = visibleSelectedIdsFrom(normalized, selectedIds);
  const uniqueStatuses = uniqueStatusesFrom(normalized);
  const allVisibleSameStatus = uniqueStatuses.length === 1;

  let anchorStatus = null;
  if (visibleSelectedIds.length) {
    const selected = new Set(visibleSelectedIds);
    const firstSelected = normalized.find((row) => selected.has(row._id));
    anchorStatus = firstSelected?.status || null;
  } else if (allVisibleSameStatus) {
    anchorStatus = uniqueStatuses[0];
  }

  const matchingIds = anchorStatus
    ? normalized
        .filter((row) => row.status === anchorStatus)
        .map((row) => row._id)
    : [];

  const selectedSet = new Set(visibleSelectedIds);
  let selectedMatchingCount = 0;
  for (const id of matchingIds) {
    if (selectedSet.has(id)) selectedMatchingCount += 1;
  }

  const isAllMatchingSelected =
    matchingIds.length > 0 && selectedMatchingCount === matchingIds.length;
  const isSomeMatchingSelected =
    selectedMatchingCount > 0 && !isAllMatchingSelected;
  const headerDisabled =
    normalized.length === 0 ||
    (!visibleSelectedIds.length && !allVisibleSameStatus);

  let headerState = HEADER_STATES.DISABLED;
  if (!headerDisabled) {
    if (isAllMatchingSelected) headerState = HEADER_STATES.MINUS;
    else if (isSomeMatchingSelected) headerState = HEADER_STATES.PLUS;
    else headerState = HEADER_STATES.UNCHECKED;
  }

  return {
    visibleSelectedIds,
    anchorStatus,
    matchingIds,
    isAllMatchingSelected,
    isSomeMatchingSelected,
    headerDisabled,
    headerState,
  };
};

export const isPromotionRowSelectable = (rowStatus, model) =>
  !model.visibleSelectedIds.length ||
  (rowStatus || "CONTACT_PENDING") === model.anchorStatus;

export const nextIdsAfterRowToggle = (row, checked, model) => {
  const status = rowStatusOf(row);
  if (checked && !isPromotionRowSelectable(status, model)) {
    return model.visibleSelectedIds;
  }
  if (checked) {
    return model.visibleSelectedIds.includes(row._id)
      ? model.visibleSelectedIds
      : [...model.visibleSelectedIds, row._id];
  }
  return model.visibleSelectedIds.filter((id) => id !== row._id);
};

export const nextIdsAfterHeaderToggle = (model) => {
  if (model.headerDisabled || !model.matchingIds.length) {
    return model.visibleSelectedIds;
  }
  if (model.isAllMatchingSelected) return [];
  return model.matchingIds;
};
